import React, {useEffect} from 'react';
import FormRequest from '../FormRequest';
import {useNavigate, useParams} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {
    useGetMyFormRequestQuery,
    useGetFormRequestQuery,
    useGetStudentNotificationFormRequestQuery,
    useSubmitFormMutation, useGetEmployeeNotificationFormRequestQuery,
    useLazyCheckContactEmailForStudentRequestQuery,
    useLazyCheckContactEmailForStudentSelfServiceQuery
} from '../../../../../api/services/formRequestsService';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import useUser from '../../../../../utility/hooks/useUser';
import PropTypes from 'prop-types';
import objectToFormData from '../../../../../utility/objectToFormData';
import {generateUUID} from '../../../../../utility/uuidUtil';
import useFileDownloader from '../../../../../utility/hooks/useFileDownloader';
import {downloadFormRequestAttachment} from '../../../../../api/services/filesService';
import {ROLES} from '../../../../../constants/roles';

const SubmitPrivateForm = ({
                               navigateBackRoute,
                               personalForm
                           }) => {
    const {user} = useUser();
    const isPersonalForm = typeof personalForm === 'function'
        ? personalForm({user})
        : personalForm;
    const {
        formRequestId,
        formId,
        studentId,
        id: employeeId
    } = useParams();
    const isStudent = !!studentId;
    const isEmployee = !!employeeId || user.hasRole(ROLES.EMPLOYEE);
    const dispatch = useDispatch();
    const [checkContactEmailForStudentRequest] =
        useLazyCheckContactEmailForStudentRequestQuery();
    const [checkContactEmailForStudentSelfService] =
        useLazyCheckContactEmailForStudentSelfServiceQuery();
    const {
        data: formRequestData,
        isLoading: isLoadingFormRequest,
        isFetching: isFetchingFormRequest,
        error: formRequestError,
        refetch: refetchFormRequest
    } = useGetFormRequestQuery(formRequestId, {skip: isPersonalForm || !formRequestId});
    const {
        data: myFormRequestData,
        isLoading: isLoadingMyFormRequestData,
        isFetching: isFetchingMyFormRequestData,
        error: myFormRequestError,
        refetch: refetchMyFormRequest
    } = useGetMyFormRequestQuery(formRequestId, {skip: !formRequestId || !isPersonalForm});
    const {
        data: studentData,
        isLoading: isLoadingStudent,
        isFetching: isFetchingStudent,
        error: errorStudent,
    } = useGetStudentNotificationFormRequestQuery(
        {
            formId,
            personId: studentId
        },
        {skip: !formId || !isStudent}
    );
    const {
        data: employeeData,
        isLoading: isLoadingEmployee,
        isFetching: isFetchingEmployee,
        error: errorEmployee,
    } = useGetEmployeeNotificationFormRequestQuery(
        {
            formId,
            personId: employeeId
        },
        {skip: !formId || !isEmployee}
    );
    const [submitForm, {
        isLoading: isFormSubmitLoading,
        isSuccess: isFormSubmitSuccess,
        isError: isFormSubmitError,
        error: submitError
    }] = useSubmitFormMutation();
    const downloadFile = useFileDownloader();
    const navigate = useNavigate();
    const generalFormData = studentData ?? employeeData;
    const isLoadingGeneralForm = isLoadingStudent || isLoadingEmployee;
    const isFetchingGeneralForm = isFetchingStudent || isFetchingEmployee;
    const generalFormError = errorStudent ?? errorEmployee;
    const isLoading = isLoadingFormRequest || isLoadingGeneralForm || isLoadingMyFormRequestData;
    const isFetching = isFetchingFormRequest || isFetchingMyFormRequestData || isFetchingGeneralForm;
    const error = formRequestError || generalFormError || myFormRequestError;
    const formRequest = formRequestData || generalFormData || myFormRequestData;
    const isElevated = user?.hasElevatedAccess?.() === true;
    const isParentRole = user?.hasRole?.(ROLES.PARENT) === true;
    const isParentView = !!isPersonalForm && !isElevated;

    useEffect(() => {
        if (isFormSubmitError || isFormSubmitSuccess) {
            if (isFormSubmitSuccess) {
                if (formRequestId) {
                    if (isPersonalForm) {
                        refetchMyFormRequest();
                    } else {
                        refetchFormRequest();
                    }
                } else {
                    navigate('submit-success', {replace: true});
                }

            }
            showNotification(isFormSubmitSuccess);
        }
    }, [isFormSubmitSuccess, isFormSubmitError]);

    const showNotification = success => success !== undefined && dispatch(
        {
            true: () => showSnackbar({
                message: `${formRequest.formName} submitted successfully`,
                severity: 'success'
            }),
            false: () => showSnackbar({
                message: submitError,
                severity: 'error'
            })
        }[success]()
    );

    const handleDownloadAttachment = async (fileId) => {
        await downloadFile(downloadFormRequestAttachment, {
            fileId,
            formRequestId,
            roles: user.roles,
            studentId: formRequest.studentId,
            shouldDownloadByCurrentUser: isPersonalForm
        });
    };

    const handleSubmit = async (data, placeholders) => {
        const responses = {};
        const attachments = [];
        const attachmentKeys = [];

        for (const key in data) {
            if (data[key] instanceof Blob) {
                const file = data[key];
                const fileKey = generateUUID();

                attachments.push(file);
                attachmentKeys.push(fileKey);
                responses[key] = {
                    name: file.name,
                    fileKey
                };
            } else {
                responses[key] = data[key];
            }
        }

        await submitForm(objectToFormData({
            id: formRequestId,
            responses: JSON.stringify(responses),
            formId,
            studentId,
            employeeId,
            attachments,
            attachmentKeys,
            placeholders
        }));
    };

    const onEmailCheck = async (email) => {
        const trimmed = (email ?? '').trim();
        if (!trimmed) return null;

        try {
            if (formRequestId) {
                return await checkContactEmailForStudentRequest({
                    requestId: Number(formRequestId),
                    email: trimmed
                }).unwrap();
            }

            if (formId && studentId) {
                return await checkContactEmailForStudentSelfService({
                    formId: Number(formId),
                    studentId: Number(studentId),
                    email: trimmed
                }).unwrap();
            }

            return null;
        } catch {
            return null;
        }
    };

    return <FormRequest
        generalForm={formId !== undefined && (studentId !== undefined || isEmployee)}
        formRequest={formRequest}
        isLoading={isLoading || isFetching}
        isSubmitting={isFormSubmitLoading}
        error={error}
        onSubmit={handleSubmit}
        onDownloadAttachment={handleDownloadAttachment}
        navigateBackRoute={navigateBackRoute}
        onEmailCheck={onEmailCheck}
        isParentView={isParentView}
        isElevated={isElevated}
        isParentRole={isParentRole}
    />
};

SubmitPrivateForm.defaultProps = {
    personalForm: false
}

SubmitPrivateForm.propTypes = {
    navigateBackRoute: PropTypes.string.isRequired,
    personalForm: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.func,
    ]),
};
export default SubmitPrivateForm;